import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import Msg91Widget from '../components/Msg91Widget';
import { AuthShell } from './Register';
import { ShieldCheck, MessageCircle, Loader2, RefreshCw, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { getAttribution } from '../lib/attribution';
import { emitSignupEvent, emitSignupEventBeacon, clearSignupSessionId } from '../lib/signupFunnel';

// Iter-52.27 — key under which Register.jsx stashes name/city/state
// right before redirecting to Google, so we can forward those fields
// to /auth/session/complete after the Google→MSG91 round-trip.
const GOOGLE_SIGNUP_STASH_KEY = 'rb_google_signup_stash';

/**
 * AuthCallback — iter-51.18.
 *
 * Handles the Emergent Google-auth redirect. Two branches:
 *
 *   • RETURNING user (server's response has `needs_mobile: false`)
 *     — behave exactly like before: set context user, navigate to
 *     /profiles.
 *
 *   • NEW Google email (server's response has `needs_mobile: true`)
 *     — server has stashed the auth material in a pending row and
 *     returned a `pending_id`. We render the MSG91 widget; on the
 *     widget's success we POST /auth/session/complete with the
 *     pending_id + access_token, which finishes the signup only
 *     after MSG91 verifies the mobile AND our `trial_mobiles`
 *     ledger clears the number.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useApp();
  const hasProcessed = useRef(false);
  const submittingRef = useRef(false);
  const [stage, setStage] = useState('exchanging'); // 'exchanging' | 'needs-mobile' | 'submitting' | 'error'
  const [pending, setPending] = useState(null); // { pending_id, email, name }
  const [error, setError] = useState('');
  const [dup, setDup] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0);
  // Iter-52.41 — gate MSG91 popup on explicit hint ack (see Register.jsx).
  const [hintAcked, setHintAcked] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false); // iter-51.24h
  // Iter-52.32 — abandonment tracking (see Register.jsx for details).
  const widgetMobileRef = useRef(null);
  const widgetOpenRef = useRef(false);
  const otpSubmittedFiredRef = useRef(false);

  // Iter-52.32 — pagehide/visibilitychange beacon for Google signup
  // abandonment. Fires only if user reached the MSG91 stage but never
  // completed / failed OTP entry.
  useEffect(() => {
    const flushAbandoned = () => {
      if (!widgetOpenRef.current) return;
      if (otpSubmittedFiredRef.current) return;
      otpSubmittedFiredRef.current = true;
      emitSignupEventBeacon('otp_submitted', {
        mode: 'google',
        status: 'failed',
        mobile_e164: widgetMobileRef.current || undefined,
        error: 'abandoned',
      });
    };
    const onPageHide = () => flushAbandoned();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushAbandoned();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');
    if (!sessionId) { navigate('/'); return; }

    (async () => {
      try {
        const { data } = await api.post('/auth/session', { session_id: sessionId });
        if (data?.needs_mobile) {
          setPending({ pending_id: data.pending_id, email: data.email, name: data.name });
          setStage('needs-mobile');
          setHintAcked(false);   // iter-52.41 — force hint ack on entry
          // Iter-52.27 — Google signup path now emits the same funnel
          // events as Register.jsx so drop-off tracking captures all
          // signup modes. otp_reached fires when the MSG91 widget
          // becomes visible.
          emitSignupEvent('otp_reached', { mode: 'google' });
          widgetOpenRef.current = true;
          otpSubmittedFiredRef.current = false;
          widgetMobileRef.current = null;
          // Scrub the session_id from the URL so a refresh doesn't
          // re-exchange it (Emergent's session_id is single-use).
          window.history.replaceState(null, '', '/');
          return;
        }
        // Returning user path — legacy behavior.
        setUser(data);
        window.history.replaceState(null, '', '/profiles');
        navigate('/profiles', { replace: true, state: { user: data } });
      } catch (e) {
        console.error('auth failed', e);
        setError(e?.response?.data?.detail || 'Sign-in failed. Please try again.');
        setStage('error');
      }
    })();
  }, [location, navigate, setUser]);

  const onWidgetVerified = async (accessToken, widgetMobile) => {
    if (!pending?.pending_id) return;
    // Same single-fire guard as Register.jsx — auth deserves belt AND
    // braces. MSG91's SDK has been observed to invoke success twice
    // on rapid double-tap; the widget guards too, this is redundancy.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setStage('submitting');
    setError(''); setDup(false);
    // Iter-52.27 — funnel: OTP submitted successfully.
    // Iter-52.31 — attach mobile from the widget shadow-DOM so the
    // drilldown Nudge button works even if the follow-up POST fails.
    emitSignupEvent('otp_submitted', {
      mode: 'google', status: 'success',
      mobile_e164: widgetMobile || widgetMobileRef.current || undefined,
    });
    otpSubmittedFiredRef.current = true;
    widgetOpenRef.current = false;
    // Iter-52.27 — read the pre-Google form stash (name/city/state)
    // + first-touch attribution and forward them to session/complete.
    let stash = {};
    try {
      const raw = window.sessionStorage.getItem(GOOGLE_SIGNUP_STASH_KEY);
      if (raw) stash = JSON.parse(raw) || {};
    } catch { /* noop */ }
    const attribution = getAttribution() || undefined;
    try {
      const { data } = await api.post('/auth/session/complete', {
        session_id: pending.pending_id,
        access_token: accessToken,
        name: (stash.name || '').trim() || undefined,
        city: (stash.city || '').trim() || undefined,
        state: (stash.state || '').trim() || undefined,
        attribution,
      });
      // Iter-52.27 — funnel: signup complete.
      emitSignupEvent('signup_completed', {
        mode: 'google', user_id: data?.user_id, mobile_e164: data?.mobile_e164,
      });
      clearSignupSessionId();
      try { window.sessionStorage.removeItem(GOOGLE_SIGNUP_STASH_KEY); } catch {}
      setUser(data);
      window.history.replaceState(null, '', '/profiles');
      navigate('/profiles', { replace: true, state: { user: data } });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Could not complete signup. Please try again.';
      if (status === 409 && /free trial/i.test(detail)) setDup(true);
      let uiMsg;
      if (status === 400 && /mobile/i.test(detail) && /email/i.test(detail)) {
        uiMsg = 'Please choose "Mobile" (not Email) at the top of the OTP popup, then re-enter your number.';
      } else {
        uiMsg = detail;
      }
      setError(uiMsg);
      // Iter-51.24f — DO NOT auto-remount the widget on error. The
      // previous flow bumped widgetKey which re-opened MSG91's popup
      // and covered the error message before the user could read it.
      // Now we park on a dedicated 'blocked' step and require a tap
      // to remount.
      // Iter-51.24g — sticky toast (no auto-dismiss). See Register.jsx
      // for the rationale — a mobile user was interpreting the toast
      // auto-dismissing as "the error disappeared".
      toast.error(uiMsg, { duration: Infinity, id: 'authcb-error' });
      submittingRef.current = false;
      setStage('blocked');
    }
  };

  if (stage === 'blocked') {
    return (
      <AuthShell>
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full bg-rose-100 mx-auto mb-3 flex items-center justify-center">
            <RefreshCw size={30} strokeWidth={2.5} className="text-rose-600"/>
          </div>
          <h1 className="text-2xl font-extrabold text-[#2B2140]">We couldn't complete your sign-up</h1>
        </div>
        <div data-testid="authcb-blocked-error"
          className="rounded-2xl bg-rose-50 border-2 border-rose-200 text-rose-800 text-sm font-semibold px-4 py-3 mb-4 leading-relaxed">
          {error}
        </div>
        {dup ? (
          <a
            href={`https://wa.me/919599668449?text=${encodeURIComponent(`Hi Leena, my mobile already used a ReadBuddy trial. I'd like to subscribe.`)}`}
            target="_blank" rel="noopener noreferrer"
            className="pushable w-full bg-[#25D366] text-white text-sm font-bold py-3 rounded-full inline-flex items-center justify-center gap-2"
          >
            <MessageCircle size={14} strokeWidth={2.6}/> Subscribe on WhatsApp
          </a>
        ) : (
          <button
            data-testid="authcb-blocked-retry"
            onClick={() => {
              setError('');
              setWidgetKey(k => k + 1);
              submittingRef.current = false;
              setHintAcked(false);   // iter-52.41 — re-show hint on retry
              setStage('needs-mobile');
              toast.dismiss('authcb-error');
            }}
            className="pushable w-full bg-[#FF6B1A] text-white text-sm font-bold py-3 rounded-full inline-flex items-center justify-center gap-2"
          >
            <RefreshCw size={14}/> Try again with a fresh OTP
          </button>
        )}

        {/* Iter-51.24h — Verification tips collapsible. Same tips as
            Register.jsx; parents landing here from social auth hit
            the same three failure modes. */}
        <button
          type="button"
          data-testid="authcb-tips-toggle"
          onClick={() => setTipsOpen(v => !v)}
          aria-expanded={tipsOpen}
          className="mt-5 w-full text-left inline-flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-bold"
        >
          <span className="inline-flex items-center gap-1.5">
            <Info size={14}/> Why did this happen? — verification tips
          </span>
          {tipsOpen ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
        </button>
        {tipsOpen && (
          <div data-testid="authcb-tips-panel" className="mt-2 rounded-xl border border-slate-200 bg-white p-3 text-[13px] text-slate-700 leading-relaxed">
            <p className="font-bold text-slate-800 mb-2">Three most common reasons:</p>
            <ul className="space-y-2 list-none">
              <li className="flex gap-2">
                <span className="text-[#FF6B1A] font-extrabold shrink-0">1.</span>
                <span>
                  <span className="font-bold">This Google account is already linked to a ReadBuddy account.</span> Try signing in from the home page — you may already have an account with us.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#FF6B1A] font-extrabold shrink-0">2.</span>
                <span>
                  <span className="font-bold">The mobile number was already used for a free trial.</span> Only one free trial per mobile is allowed. Tap <span className="font-bold">Subscribe on WhatsApp</span> if you'd like to continue on a paid plan.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#FF6B1A] font-extrabold shrink-0">3.</span>
                <span>
                  <span className="font-bold">You picked "Email" instead of "Mobile" in the OTP popup.</span> When the popup opens, make sure the top tab says <span className="font-bold">Mobile</span> (not Email) — we can only verify mobile numbers for now.
                </span>
              </li>
            </ul>
            <p className="mt-3 text-[11px] text-slate-500">
              Still stuck? <a
                href={`https://wa.me/919599668449?text=${encodeURIComponent(`Hi Leena, I'm having trouble creating my ReadBuddy account. The error was: ${error}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="underline font-bold text-emerald-700"
              >WhatsApp Leena</a> and we'll get you sorted.
            </p>
          </div>
        )}
      </AuthShell>
    );
  }

  if (stage === 'needs-mobile' || stage === 'submitting') {
    const submitting = stage === 'submitting';
    return (
      <AuthShell>
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-3 flex items-center justify-center">
            {submitting
              ? <Loader2 size={30} strokeWidth={2.5} className="text-emerald-600 animate-spin"/>
              : <ShieldCheck size={30} strokeWidth={2.5} className="text-emerald-600"/>}
          </div>
          <h1 data-testid="google-signup-mobile-header" className="text-2xl font-extrabold text-[#2B2140]">
            {submitting ? 'Creating your account…' : 'One last step'}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {submitting
              ? 'Confirming with MSG91 and finalizing your ReadBuddy trial.'
              : (<>Signed in as <span className="font-bold">{pending?.email}</span>. Please verify your mobile with a 4-digit OTP to start your free trial.</>)}
          </p>
        </div>
        {/* Iter-52.39/41 — bilingual WhatsApp hint + ack gate (see Register.jsx). */}
        <div
          data-testid="whatsapp-hint"
          className="mb-3 px-4 py-3 rounded-2xl bg-[#25D366]/10 border-2 border-[#25D366]/40 text-[14px] text-[#0F5132] leading-snug"
        >
          <div className="font-extrabold flex items-start gap-2">
            <span className="text-lg leading-none">📱</span>
            <span>Please enter your <span className="underline decoration-2 decoration-[#25D366] underline-offset-2">WhatsApp number</span></span>
          </div>
          <div className="text-[12px] text-[#0F5132]/85 font-hi mt-1 ml-6">कृपया अपना WhatsApp नंबर दर्ज करें</div>
          <div className="text-[11px] text-[#0F5132]/70 mt-1.5 ml-6">
            We'll send OTP + trial reminders on WhatsApp.
          </div>
        </div>
        {!hintAcked && !submitting && (
          <button
            data-testid="whatsapp-hint-ack"
            onClick={() => setHintAcked(true)}
            className="pushable w-full bg-[#25D366] hover:bg-[#20BF5B] text-white text-base font-bold py-3 rounded-full inline-flex items-center justify-center gap-2"
          >
            I got it — verify my WhatsApp number
          </button>
        )}
        {(hintAcked || submitting) && (
          <div className={submitting ? 'pointer-events-none opacity-40' : ''}>
            <Msg91Widget
              key={widgetKey}
              onVerified={onWidgetVerified}
              onMobileTyped={(m) => { if (m) widgetMobileRef.current = m; }}
              onError={(m, widgetMobile) => {
                if (!submittingRef.current) {
                  // Iter-52.27 — funnel: OTP entry failed.
                  // Iter-52.31 — carry mobile from widget shadow-DOM.
                  otpSubmittedFiredRef.current = true;
                  emitSignupEvent('otp_submitted', {
                    mode: 'google', status: 'failed',
                    mobile_e164: widgetMobile || widgetMobileRef.current || undefined,
                    error: (m || '').slice(0, 200),
                  });
                  setError(m);
                }
              }}
              label="Continue in MSG91 popup"
            />
          </div>
        )}
        {submitting && (
          <p data-testid="google-signup-submitting" className="text-sm text-emerald-700 text-center mt-4 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin"/> Almost done — creating your account…
          </p>
        )}
        {!submitting && error && (
          <p data-testid="google-signup-error" className="text-sm text-rose-600 mt-4 text-center">{error}</p>
        )}
        {!submitting && error && dup && (
          <a data-testid="google-signup-dup-whatsapp"
            href="https://wa.me/919599668449?text=Hi%20Leena%2C%20my%20mobile%20already%20used%20a%20ReadBuddy%20trial.%20I%27d%20like%20to%20subscribe."
            target="_blank" rel="noopener noreferrer"
            className="pushable mt-4 w-full bg-[#25D366] text-white text-sm font-bold py-2.5 rounded-full inline-flex items-center justify-center gap-2">
            <MessageCircle size={14} strokeWidth={2.6}/> Subscribe on WhatsApp
          </a>
        )}
      </AuthShell>
    );
  }

  if (stage === 'error') {
    return (
      <AuthShell>
        <h1 className="text-2xl font-extrabold text-[#2B2140] mb-2">Sign-in failed</h1>
        <p data-testid="google-signup-fatal" className="text-sm text-rose-600 mb-4">{error}</p>
        <button data-testid="google-signup-fatal-back" onClick={() => navigate('/', { replace: true })}
          className="pushable w-full bg-[#5B8DEF] text-white text-lg font-bold py-3 rounded-full">
          Back to home
        </button>
      </AuthShell>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#FF6B1A] border-t-transparent rounded-full animate-spin mx-auto mb-4"/>
        <p className="text-slate-600 font-bold">Signing you in…</p>
      </div>
    </div>
  );
}
